import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { defaultDirFor, relativeTime, formatDateTime } from './projectListModel';
import { NameLink, Owner, ReviewBar, SourceBadge, VendorMark, VersionPath } from './ProjectAtoms';
import { ProjectNameInput } from './ProjectNameEditor';
import ProjectActionsMenu from './ProjectActionsMenu';
import ProjectDetail from './ProjectDetail';

const COLUMNS = [
  { key: 'name', label: 'Project', sort: 'name' },
  { key: 'versions', label: 'Baseline → target', className: 'pl-col-vers' },
  { key: 'review', label: 'Review', sort: 'review' },
  { key: 'source', label: 'Source', sort: 'fleet' },
  { key: 'owner', label: 'Owner', sort: 'owner', className: 'pl-col-owner' },
  { key: 'activity', label: 'Last activity', sort: 'activity' }
];

const Chevron = () => (
  <svg
    className="bz-th-sort__chev"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M2 3.5l3 3 3-3" />
  </svg>
);

const ProjectTable = ({ projects, ctx, sort, onSort, expandedId, editingId, canModify, actions }) => {
  const colCount = COLUMNS.length + 1;
  const sortBy = key =>
    onSort(sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: defaultDirFor(key) });

  return (
    <div className="bz-card pl-tablecard">
      <table className="bz-table pl-table">
        <thead>
          <tr>
            {COLUMNS.map(col =>
              col.sort ? (
                <th
                  key={col.key}
                  className={`bz-th-sort${col.className ? ` ${col.className}` : ''}`}
                  aria-sort={sort.key === col.sort ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button type="button" onClick={() => sortBy(col.sort)}>
                    {col.label}
                    <Chevron />
                  </button>
                </th>
              ) : (
                <th key={col.key} className={col.className}>
                  {col.label}
                </th>
              )
            )}
            <th className="pl-col-menu" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {projects.map(project => {
            const open = project.id === expandedId;
            return (
              <Fragment key={project.id}>
                <tr
                  className={`pl-trow${open ? ' is-expanded' : ''}`}
                  data-pid={project.id}
                  aria-expanded={open}
                  onClick={() => actions.toggle(project)}
                  onDoubleClick={() => actions.open(project)}
                >
                  <td>
                    <div className="pl-tproj">
                      <span className={`pl-caret${open ? ' is-open' : ''}`} aria-hidden="true">
                        ›
                      </span>
                      <VendorMark vendorId={project.vendorId} label={ctx.vendorLabel(project.vendorId)} size={28} />
                      <div className="pl-tproj__txt">
                        <span className="pl-tproj__name bz-truncate" title={project.name}>
                          {editingId === project.id ? (
                            <ProjectNameInput
                              name={project.name}
                              onSave={name => actions.saveName(project, name)}
                              onCancel={actions.cancelRename}
                            />
                          ) : (
                            <NameLink project={project} onOpen={actions.open} />
                          )}
                        </span>
                        <span className="pl-tproj__sub bz-truncate">
                          {ctx.vendorLabel(project.vendorId)} · {project.productName}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="pl-col-vers">
                    <VersionPath project={project} versionLabel={ctx.versionLabel} compact />
                  </td>
                  <td className="pl-col-review">
                    <ReviewBar project={project} available={ctx.availableVersions(project)} compact />
                  </td>
                  <td>
                    <SourceBadge project={project} />
                  </td>
                  <td className="pl-nowrap pl-col-owner">
                    <Owner userId={project.userId} currentUserEmail={ctx.currentUserEmail} avatarOnly />
                  </td>
                  <td className="pl-nowrap" title={formatDateTime(project.lastActivityAt || project.updatedAt)}>
                    {relativeTime(project.lastActivityAt || project.updatedAt)}
                  </td>
                  <td className="pl-col-menu">
                    <ProjectActionsMenu project={project} canModify={canModify} actions={actions} />
                  </td>
                </tr>
                {open && (
                  <tr className="pl-xrow">
                    <td colSpan={colCount}>
                      <ProjectDetail project={project} ctx={ctx} canModify={canModify} actions={actions} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

ProjectTable.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  ctx: PropTypes.object.isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
  expandedId: PropTypes.string,
  editingId: PropTypes.string,
  canModify: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired
};

export default ProjectTable;
